import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { QueryClient, type Query } from '@tanstack/react-query';
import { persistQueryClient } from '@tanstack/react-query-persist-client';
import { AUTH_TOKEN_STORAGE_KEY } from '../api/config';

const ONE_MINUTE = 60 * 1000;
const THIRTY_MINUTES = 30 * 60 * 1000;
const CACHE_MAX_AGE = 24 * 60 * 60 * 1000; // 24 hours
const CACHE_KEY_PREFIX = 'odo-query-cache';

// DM scan URLs are HMAC-signed with a 1h TTL; persisting them past that would
// rehydrate dead images, so these refetch on every mount instead.
function isDmScanQuery(queryKey: readonly unknown[]): boolean {
    return queryKey[0] === 'patient' && queryKey[2] === 'monitoring' && queryKey[3] === 'scans';
}

function shouldDehydrateQuery(query: Query): boolean {
    if (isDmScanQuery(query.queryKey)) return false;
    return query.state.status === 'success';
}

export const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: ONE_MINUTE,
            gcTime: THIRTY_MINUTES,
            refetchOnMount: true,
            refetchOnWindowFocus: false,
            refetchOnReconnect: true,
            retry: 2,
            retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000)
        }
    }
});

// Storage can throw outright (Safari private mode, blocked third-party
// contexts). This module runs above ErrorBoundary, so it must never throw.
function safeStorage<T>(read: () => T, fallback: T): T {
    try {
        return read();
    } catch {
        return fallback;
    }
}

// FNV-1a: a 32-bit label distinguishing one bearer token from another, not a
// secret - the token itself already lives in localStorage.
function fingerprint(value: string): string {
    let hash = 0x811c9dc5;
    for (let i = 0; i < value.length; i++) {
        hash ^= value.charCodeAt(i);
        hash = Math.imul(hash, 0x01000193);
    }
    return (hash >>> 0).toString(36);
}

/** Whose cache this is. 'anon' covers the signed-out window (login screen). */
function currentIdentity(): string {
    const token = safeStorage(() => localStorage.getItem(AUTH_TOKEN_STORAGE_KEY), null);
    return token ? fingerprint(token) : 'anon';
}

// Identity lives in the storage KEY, not the buster: a buster is only read at
// module load, and login navigates client-side without reloading.
function cacheKeyFor(identity: string): string {
    return `${CACHE_KEY_PREFIX}:${identity}`;
}

// Delete every bucket except `keep`, so no identity's rows outlive its session.
function pruneOtherCaches(keep: string | null): void {
    safeStorage(() => {
        for (let i = localStorage.length - 1; i >= 0; i--) {
            const key = localStorage.key(i);
            if (key && key.startsWith(CACHE_KEY_PREFIX) && key !== keep) {
                localStorage.removeItem(key);
            }
        }
        return null;
    }, null);
}

let unsubscribe: (() => void) | undefined;
let activePersister: ReturnType<typeof createSyncStoragePersister> | undefined;
let armedIdentity: string | undefined;
let writesRevoked = false;

// Revoked at the storage boundary: the persister's trailing save has no cancel
// handle. Delegates, not spreads - Storage's own enumerable props are the data.
const revocableStorage: Storage = {
    get length() {
        return safeStorage(() => window.localStorage.length, 0);
    },
    key: (index) => safeStorage(() => window.localStorage.key(index), null),
    clear: () => safeStorage(() => window.localStorage.clear(), undefined),
    getItem: (key) => safeStorage(() => window.localStorage.getItem(key), null),
    setItem: (key, value) => {
        if (writesRevoked) return;
        safeStorage(() => window.localStorage.setItem(key, value), undefined);
    },
    removeItem: (key) => safeStorage(() => window.localStorage.removeItem(key), undefined)
};

// (Re)bind persistence to the current identity; needed after login, which
// changes who is signed in without reloading.
export function armPersistence(): void {
    const identity = currentIdentity();
    if (unsubscribe && identity === armedIdentity) {
        return;
    }
    unsubscribe?.();
    armedIdentity = identity;
    writesRevoked = false;
    const key = cacheKeyFor(identity);
    pruneOtherCaches(key);
    activePersister = createSyncStoragePersister({ storage: revocableStorage, key });
    // Busting on build id drops the cache whenever the bundle changes, so a
    // payload-shape change cannot reach consumers as a rehydrated old object.
    const [unsub, restored] = persistQueryClient({
        queryClient,
        persister: activePersister,
        maxAge: CACHE_MAX_AGE,
        buster: __APP_VERSION__,
        dehydrateOptions: { shouldDehydrateQuery }
    });
    unsubscribe = unsub;
    // A corrupt bucket makes persistQueryClient reject before it subscribes.
    // Drop it and reset, so the next arm (login, logout) can bind again.
    void restored.catch(() => {
        void activePersister?.removeClient();
        unsubscribe = undefined;
        armedIdentity = undefined;
    });
}

// Drop every persisted bucket and the in-memory cache, on logout.
export function clearPersistedQueryCache(): void {
    // Revoke before removing: a throttled save may already be scheduled, and
    // nothing in the persister API can cancel it.
    writesRevoked = true;
    unsubscribe?.();
    unsubscribe = undefined;
    armedIdentity = undefined;
    queryClient.clear();
    void activePersister?.removeClient();
    activePersister = undefined;
    pruneOtherCaches(null);
}

armPersistence();

// Another tab switching practice (or signing out) leaves this one holding the
// previous identity's in-memory cache, which no reload here would correct.
window.addEventListener('storage', (event) => {
    if (event.key === AUTH_TOKEN_STORAGE_KEY && event.oldValue !== event.newValue) {
        window.location.reload();
    }
});
